import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { ComponentStore } from '@ngrx/component-store'

import * as state from '../../../../../../../state'

import type { Widget } from './widget'

export interface FieldCollectionState {
  /**
   * Maps field IDs to their corresponding values. Values can be accessed using
   * {@link ents.core.display.Field.Id} identifiers. Since values are stored as
   * {@link ents.common.value.Raw}, we use `unknown` type for flexibility.
   */
  fieldValueLookup: Partial<Record<ents.core.display.Field.Id, unknown>>
}
export class FieldCollection<
  TType extends ents.core.mosaic.Widget.Type,
> extends ComponentStore<FieldCollectionState> {
  /** @see {@link Widget} */
  private readonly _widgetStore
  /** @see {@link state.mosaic.endUser.currentRendering.Feature} */
  private readonly _currentRendering = ngCore.inject(
    state.mosaic.endUser.currentRendering.Feature,
  )
  /** @see {@link state.mosaic.dev.actions.Collection} */
  private readonly _actionsEc = ngCore.inject(
    state.mosaic.dev.actions.Collection,
  )
  /**
   * Returns the save action associated with this widget, if one exists, otherwise
   * returns `undefined`
   */
  public readonly onSaveActionSg = ngCore.computed(
    (
      content = this._widgetStore.contentSg() satisfies
        | ents.core.mosaic.Widget.Content
        | undefined as ents.core.display.Field.Collection | undefined,
    ) => content?.onSave,
  )
  /** TODO:  */
  // TODO: have a type for onSave, and then use the type checker, rather then
  // flattened type.
  public readonly hasOnSaveActionSg = ngCore.computed(
    (onSaveAction = this.onSaveActionSg()) => !!onSaveAction,
  )

  /** TODO: Big explanation here needed */
  private readonly _substitutedOnSaveActionSg = ngCore.computed(
    (onSaveAction = this.onSaveActionSg()) => {
      if (!onSaveAction) {
        return undefined
      }
      const { variables } = onSaveAction
      const actionVariables = variables ?? {}
      const fieldValueLookup = this.fieldValueLookupSg()

      const replacedVariables =
        ents.core.mosaic.Action.Ref.UserInputVariableValue.replaceAll(
          actionVariables,
          ents.core.display.Field.OnSave.UserInputVariableValueName.Field.lookupFrom(
            fieldValueLookup,
          ),
        )

      return {
        ...onSaveAction,
        variables: replacedVariables,
      }
    },
  )

  /** @see {@link FieldCollectionState.fieldValueLookup} */
  public readonly fieldValueLookupSg = this.selectSignal(
    s => s.fieldValueLookup,
  )

  constructor({ widgetStore }: { widgetStore: Widget<TType> }) {
    super({ fieldValueLookup: {} })
    this._widgetStore = widgetStore
  }

  /**
   * Add/Updates the value in {@link AtnMosaicWidgetState.fieldValueLookup} for a given
   * {@link ents.core.display.Field.Id}.
   */
  public setFieldValue(fieldId: ents.core.display.Field.Id, value: unknown) {
    this.patchState(s => ({
      fieldValueLookup: { ...s.fieldValueLookup, [fieldId]: value },
    }))
  }

  /** TODO: */
  public executeOnSaveAction(source: string): void {
    const onSaveAction = this._substitutedOnSaveActionSg()

    if (!onSaveAction) {
      // TODO: what to do in this case?
      console.warn('No onSave action found')
      return
    }

    // TODO: how to close modal, only after the action is executed?
    this._currentRendering.subfeatures.modal.actions.setModal
      .from(source)
      .dispatch({ widgetRef: undefined })

    const context = this._currentRendering.selectors.context.asSignal()
    const widgetId = this._widgetStore.renderingIdSg()
    const refId = this._widgetStore.refIdSg()

    if (context && widgetId) {
      this._currentRendering.subfeatures.actions
        .createActionHandler(source, {
          ...context,
          actionRef: onSaveAction,
          widget: {
            id: widgetId,
            refId,
          },
        })
        .execute()
    }
  }
}
